import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUf, type EstadoSelecionado } from '@/shared/components/mapa-uf/mapa-uf';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { AuthService } from '@/core/auth/auth.service';
import { MedicoControleAdministrativoFeatureService } from './medico-controle-administrativo.service';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

type ResumoMapaItem = {
    label: string;
    valor: number | null;
    /** Unidade exibida abaixo do valor. Sem isso, o card assume "registros". */
    unidade?: string;
    /** A API só apura o indicador quando a UF é informada; sem UF o card fica oculto. */
    exigeUf?: boolean;
};

type ResumoMapa = {
    titulo: string;
    descricao: string;
    grupo: string;
    classeGrupo: string;
    total: number | null;
    itens: ResumoMapaItem[];
};

type ResumoTotais = {
    inscricaoPrincipal: number | null;
    inscricaoSecundaria: number | null;
    inscricaoProvisoria: number | null;
    transferenciasConcedidas: number | null;
    baixasExercicioProfissional: number | null;
    cancelamentosInscricao: number | null;
    titulosEspecialistaEmitidos: number | null;
    ultimoCrmCadastrado: number | null;
};

type EstadoResumo = {
    sigla: string;
    nome: string;
};

@Component({
    selector: 'app-mapa-brasil',
    standalone: true,
    imports: [CommonModule, CardModule, TagModule, MapaUf, SelectEstado, SkeletonModule, CardContent, PainelLateral],
    templateUrl: './mapa-brasil.html',
    styleUrl: './mapa-brasil.scss'
})
export class MapaBrasil implements OnInit {
    private resumoRequestId = 0;
    private readonly estadosResumo: EstadoResumo[] = [
        { sigla: 'AC', nome: 'Acre' },
        { sigla: 'AL', nome: 'Alagoas' },
        { sigla: 'AP', nome: 'Amapá' },
        { sigla: 'AM', nome: 'Amazonas' },
        { sigla: 'BA', nome: 'Bahia' },
        { sigla: 'CE', nome: 'Ceará' },
        { sigla: 'DF', nome: 'Distrito Federal' },
        { sigla: 'ES', nome: 'Espírito Santo' },
        { sigla: 'GO', nome: 'Goiás' },
        { sigla: 'MA', nome: 'Maranhão' },
        { sigla: 'MT', nome: 'Mato Grosso' },
        { sigla: 'MS', nome: 'Mato Grosso do Sul' },
        { sigla: 'MG', nome: 'Minas Gerais' },
        { sigla: 'PA', nome: 'Pará' },
        { sigla: 'PB', nome: 'Paraíba' },
        { sigla: 'PR', nome: 'Paraná' },
        { sigla: 'PE', nome: 'Pernambuco' },
        { sigla: 'PI', nome: 'Piauí' },
        { sigla: 'RJ', nome: 'Rio de Janeiro' },
        { sigla: 'RN', nome: 'Rio Grande do Norte' },
        { sigla: 'RS', nome: 'Rio Grande do Sul' },
        { sigla: 'RO', nome: 'Rondônia' },
        { sigla: 'RR', nome: 'Roraima' },
        { sigla: 'SC', nome: 'Santa Catarina' },
        { sigla: 'SP', nome: 'São Paulo' },
        { sigla: 'SE', nome: 'Sergipe' },
        { sigla: 'TO', nome: 'Tocantins' }
    ];

    tituloMapa = 'Mapa Cadastro Brasil';
    resumoMapa!: ResumoMapa;
    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    private ufTokenRestrita: string | null = null;
    private totaisResumo: ResumoTotais = this.criarTotaisResumoVazios();

    constructor(
        private medicoControleAdministrativoService: MedicoControleAdministrativoFeatureService,
        private destroyRef: DestroyRef,
        private authService: AuthService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.resumoMapa = this.criarResumoMapa();

        if (this.ufTokenRestrita) {
            this.onUfSelecionadaChange(this.ufTokenRestrita);
            return;
        }

        this.onUfSelecionadaChange(this.ufSelecionada);
    }

    onEstadoSelecionado(estado: EstadoSelecionado | null): void {
        if (this.ufTokenRestrita && (!estado?.uf || estado.uf !== this.ufTokenRestrita)) {
            const nomeEstadoRestrito = this.obterNomeEstadoPorUf(this.ufTokenRestrita) ?? this.ufTokenRestrita;
            estado = { uf: this.ufTokenRestrita, nome: nomeEstadoRestrito };
        }

        if (!estado) {
            this.ufSelecionada = null;
            this.ufSelecaoGestao.definir(this.ufSelecionada);
            this.tituloMapa = 'Mapa do Brasil';
            this.resumoMapa = {
                ...this.resumoMapa,
                titulo: 'Resumo Nacional'
            };
            this.carregarResumoMapa();
            return;
        }

        this.ufSelecionada = estado.uf ?? null;
        this.ufSelecaoGestao.definir(this.ufSelecionada);
        this.tituloMapa = `Mapa de ${estado.nome}`;
        this.resumoMapa = {
            ...this.resumoMapa,
            titulo: `Resumo ${estado.nome}`
        };
        this.carregarResumoMapa(estado.uf);
    }

    onUfSelecionadaChange(uf?: string | null): void {
        const ufNormalizada = this.normalizarUfSelecionada(uf);

        if (!ufNormalizada) {
            this.onEstadoSelecionado(null);
            return;
        }

        const nomeEstado = this.obterNomeEstadoPorUf(ufNormalizada) ?? ufNormalizada;
        this.onEstadoSelecionado({ uf: ufNormalizada, nome: nomeEstado });
    }

    private criarResumoMapa(): ResumoMapa {
        const totais = this.criarTotaisResumoVazios();
        return {
            titulo: 'Resumo Nacional',
            descricao: 'Soma dos movimentos de inscrição, transferência, baixa, cancelamento e título no período.',
            grupo: 'Dados',
            classeGrupo: 'grupo-dados',
            total: this.calcularTotalResumo(totais),
            itens: this.criarItensResumo(totais)
        };
    }

    private criarTotaisResumoVazios(): ResumoTotais {
        return {
            inscricaoPrincipal: null,
            inscricaoSecundaria: null,
            inscricaoProvisoria: null,
            transferenciasConcedidas: null,
            baixasExercicioProfissional: null,
            cancelamentosInscricao: null,
            titulosEspecialistaEmitidos: null,
            ultimoCrmCadastrado: null
        };
    }

    private criarItensResumo(totais: ResumoTotais): ResumoMapaItem[] {
        return [
            { label: 'Médicos com Inscrição Principal', valor: totais.inscricaoPrincipal },
            { label: 'Médicos com Inscrição Secundária', valor: totais.inscricaoSecundaria },
            { label: 'Médicos com Inscrição Provisória', valor: totais.inscricaoProvisoria },
            { label: 'Transferências Concedidas', valor: totais.transferenciasConcedidas },
            { label: 'Baixa do Exercício Profissional (Óbito)', valor: totais.baixasExercicioProfissional },
            { label: 'Cancelamento da Inscrição', valor: totais.cancelamentosInscricao },
            { label: 'Título de Especialista Registrado', valor: totais.titulosEspecialistaEmitidos },
            { label: 'Último CRM cadastrado', valor: totais.ultimoCrmCadastrado, unidade: 'número do CRM', exigeUf: true }
        ];
    }

    /** Oculta os cards que a API só apura por UF enquanto a seleção estiver em Brasil. */
    obterItensVisiveis(itens: ResumoMapaItem[]): ResumoMapaItem[] {
        return itens.filter((item) => !item.exigeUf || !!this.ufSelecionada);
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR') {
            return this.ufTokenRestrita;
        }

        if (!/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }

    private carregarResumoMapa(uf?: string): void {
        const requestId = ++this.resumoRequestId;
        this.totaisResumo = this.criarTotaisResumoVazios();
        this.resumoMapa = {
            ...this.resumoMapa,
            total: this.calcularTotalResumo(this.totaisResumo),
            itens: this.criarItensResumo(this.totaisResumo)
        };

        this.carregarTotalResumo('inscricaoPrincipal', () => this.medicoControleAdministrativoService.consultarTotalInscricaoPrincipal(uf), requestId);
        this.carregarTotalResumo('inscricaoSecundaria', () => this.medicoControleAdministrativoService.consultarTotalInscricaoSecundaria(uf), requestId);
        this.carregarTotalResumo('inscricaoProvisoria', () => this.medicoControleAdministrativoService.consultarTotalInscricaoProvisoria(uf), requestId);
        this.carregarTotalResumo('transferenciasConcedidas', () => this.medicoControleAdministrativoService.consultarTotalTransferenciasConcedidas(uf), requestId);
        this.carregarTotalResumo('baixasExercicioProfissional', () => this.medicoControleAdministrativoService.consultarTotalBaixasExercicioProfissional(uf), requestId);
        this.carregarTotalResumo('cancelamentosInscricao', () => this.medicoControleAdministrativoService.consultarTotalCancelamentosInscricao(uf), requestId);
        this.carregarTotalResumo('titulosEspecialistaEmitidos', () => this.medicoControleAdministrativoService.consultarTotalTitulosEspecialistaEmitidos(uf), requestId);
        this.carregarTotalResumo('ultimoCrmCadastrado', () => this.medicoControleAdministrativoService.consultarUltimoCrmCadastrado(uf), requestId);
    }

    private obterNomeEstadoPorUf(uf: string): string | null {
        return this.estadosResumo.find((estado) => estado.sigla === uf)?.nome ?? null;
    }

    private carregarTotalResumo(chave: keyof ResumoTotais, consulta: () => Observable<number>, requestId: number): void {
        consulta()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (total) => this.atualizarTotalResumo(chave, total ?? 0, requestId),
                error: () => this.atualizarTotalResumo(chave, 0, requestId)
            });
    }

    private atualizarTotalResumo(chave: keyof ResumoTotais, total: number, requestId: number): void {
        if (requestId !== this.resumoRequestId) {
            return;
        }

        this.totaisResumo = {
            ...this.totaisResumo,
            [chave]: total
        };

        this.resumoMapa = {
            ...this.resumoMapa,
            total: this.calcularTotalResumo(this.totaisResumo),
            itens: this.criarItensResumo(this.totaisResumo)
        };

        // markForCheck: estado assíncrono gravado em campos não-signal sob layout OnPush não seria renderizado sem marcar o caminho como dirty.
        this.cdr.markForCheck();
    }

    private calcularTotalResumo(totais: ResumoTotais): number | null {
        const valores = [totais.inscricaoPrincipal, totais.inscricaoSecundaria, totais.inscricaoProvisoria, totais.transferenciasConcedidas, totais.baixasExercicioProfissional, totais.cancelamentosInscricao, totais.titulosEspecialistaEmitidos];

        if (!valores.every((valor): valor is number => valor !== null && valor !== undefined)) {
            return null;
        }

        return valores.reduce((total, valor) => total + valor, 0);
    }
}
